import React, { useEffect, useState, useRef } from 'react';
import { injectIntl } from 'react-intl';
import Konva from 'konva';
import { Stage, Layer, Group, Rect } from 'react-konva';
import { shuffleArray, i18nIntl, loadKonvaImage, getSoundPropsById } from '../../lib/ep.js';
import { Howl } from 'howler';
import { ExternalParticles, Particles } from './Particles.js';
import EditableArea from '../canvas/EditableArea.js';
import { getAbsoluteCoords, getRelativeCoords } from '../canvas/utils.js';

const standard_board_size = 600;	// px
const guides_color = "#FF0000";

const default_images_path = `${window.static_domain_1}img/games_gui/reveal/`;

const scaleImage = (img, width) => {
	return (width + 1) / (Math.max(img.image_w, img.image_h));
};

const ScratchableElement = ({ refs, brush_image, ...props }) => {
	const data_in_each_pixel = 4; // r, g, b, a
	const sep = props?.item_separation ?? 16;

	const brush_w = 80;
	const brush_h = 50;

	const is_scratching = useRef(false);
	const context = useRef(false);
	const viewed = useRef(false);
	const revealed = useRef(false);
	let lastPoint = null;

	const getSource = () => {
		const ctx = getCanvasContext();
		const retina = ctx.getCanvas().pixelRatio;
		const im_x = lastPoint.x * retina - 5//state.mousePos[0] * retina - 50;
		const im_y = lastPoint.y * retina - 50//state.mousePos[1] * retina - 50;
		const im_w = 5//state.particleSize[0];
		const im_h = 5//state.particleSize[1];
		return ctx.getImageData(im_x, im_y, im_w, im_h);
	};

	const start = (e) => {
		props.onStartAction(props.index);

		is_scratching.current = true;
		const stage = e.target.getStage();
		lastPoint = stage.getPointerPosition();
		refs.current['particles'].down(e, getSource());
		//Scratch a bit on single click
		lastPoint.x -= 25;
		lastPoint.y -= 25;
		if(!viewed.current) {
			props.paintUnder(props.index);
			viewed.current = true;
		}
		const ctx = getCanvasContext();
		const currentPoint = stage.getPointerPosition();
		scratch(ctx, currentPoint, brush_image.current);
	};

	const end = (e) => {
		is_scratching.current = false;

		const ctx = getCanvasContext();
		ctx.globalCompositeOperation = 'source-over';
		refs.current['particles'].up(e);
		if(!revealed.current) {
			revealed.current = true;
			props.onMouseUp();
		}
	};

	const getCanvasContext = () => {
		if(context.current != false) return context.current;
		if(refs.current['scratch_layer_ref'] == undefined) return false;
		context.current = refs.current['scratch_layer_ref'].getCanvas().getContext();
		return context.current;
	};

	const handleMove = (e) => {
		if(!is_scratching.current) return;

		const stage = e.target.getStage();
		const currentPoint = stage.getPointerPosition();
		const ctx = getCanvasContext();
		if(!ctx) return false;

		refs.current['particles'].move(e);
		scratch(ctx,currentPoint,brush_image.current);
		if(!revealed.current && getFilledInPixels(8 * data_in_each_pixel) > 40) {
			revealed.current = true;
			props.onMouseUp();
		}
	};

	const scratch = (ctx, currentPoint, brush_image) => {
		const area = props.item_grid.current[props.index].getClientRect();
		const bounds = {
			x1:area.x,
			y1:area.y,
			x2:area.x + area.width,
			y2:area.y + area.height,
		};

		let x = currentPoint.x;
		let y = currentPoint.y;
		let brush_x = Math.min(Math.max(x, bounds.x1 - sep / 2 + brush_w / 2), bounds.x2 + sep / 2 - brush_w / 2);
		let brush_y = Math.min(Math.max(y, bounds.y1 - sep / 2 + brush_h / 2), bounds.y2 + sep / 2 - brush_h / 2);

		ctx.globalCompositeOperation = 'destination-out';
		ctx.drawImage(brush_image, brush_x - brush_w / 2, brush_y - brush_h / 2);
	};

	const getFilledInPixels = (increment) => {
		if(!increment || increment < 1) increment = 1;
		if(refs.current['scratch_layer_ref'] == undefined) return 0;

		const alpha_threshold = 10;	// range: 0-255
		const ctx = getCanvasContext();
		if(!ctx) return false;

		const retina = ctx.getCanvas().pixelRatio;
		const area = props.item_grid.current[props.index].parent.getClientRect();
		const pixels = ctx.getImageData(area.x*retina, area.y*retina, area.width*retina, area.height*retina);
		const pixel_data = pixels.data;
		const pixel_length = pixel_data.length;
		const total = pixel_length / increment;
		let count = 0;
		for(let i = 0; i < pixel_length; i += increment) {
			// we only take in account the 4th value of each pixel (the alpha value)
			// to avoid issues with black cover images:
			if(parseInt(pixel_data[i + 3]) < alpha_threshold) count++;
		}
		const percent = Math.round((count / total) * 100);
		return percent;
	};

	return (
		<Rect
			{...props}
			ref={props.reference}
			onMouseDown={start}
			onTouchStart={start}
			onMouseUp={end}
			onTouchEnd={end}
			onMousemove={handleMove}
			onTouchMove={handleMove}
		/>
	)
}

const RevealWinCanvas = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const prizeType = props.prizeType;

	let stage = props.stage;
	const [ size, setSize ] = useState(props?.canvas_size ?? standard_board_size);
	const [ force_flow, setForceFlow ] = useState(false);
	const [ cover_loaded, setCoverLoaded ] = useState(false);
	const [ is_game_finished, setIsGameFinished ] = useState(false);

	const is_builder = props?.is_builder ?? false;
	const is_editing = (is_builder) ? (props?.is_editing ?? false) : false;
	const is_playable = props?.is_playable ?? true;
	const show_grid = (!is_playable && is_editing) ? true : false;
	const end_animation_max_count = 3;

	const silence = stage?.silence ?? false;

	const scale = useRef(1);
	const refs = useRef([]);
	const images = useRef({});
	const gridRef = useRef([]);
	const prizeGridRef = useRef([]);
	const gridUnderRef = useRef([]);
	const scratchRef = useRef([]);
	const userPath = useRef(0);
	const path = useRef([]);
	const grid = useRef([]);
	const hits = useRef(0);
	const hit_items = useRef([]);
	const auto_open_items = useRef([]);
	const brush_image = useRef(false);
	const scratching_index = useRef(false);

	const sounds = useRef({});
	useEffect(() => {
		if(silence) return;

		sounds.current.sound_prize = new Howl({ ...getSoundPropsById('applause') });
		sounds.current.sound_no_prize = new Howl({ ...getSoundPropsById('oh') });
		sounds.current.sound_is_ok = new Howl({ ...getSoundPropsById('flag') });
		sounds.current.sound_is_ko = new Howl({ ...getSoundPropsById('error_buzzer') });
	}, []);

	const soundOK = () => {
		if(silence) return;
		sounds.current.sound_prize.play();
	};
	const soundKO = () => {
		if(silence) return;
		sounds.current.sound_no_prize.play();
	};
	const soundIsChestOK = () => {
		if(silence) return;
		sounds.current.sound_is_ok.play();
	};
	const soundIsChestKO = () => {
		if(silence) return;
		sounds.current.sound_is_ko.play();
	};

	const cols = stage?.reveal_size ?? 3;
	const editing_mode = stage?.reveal_pos_mode ?? ((stage.reveal_individual_area != undefined) ? 'manual' : 'block');
	const items_to_reveal = parseInt(stage?.reveal_num ?? 3);
	const max_tries = parseInt(stage?.reveal_tries) || ((items_to_reveal == 1) ? 3 : cols + 2);
	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };
	const reveal_type = stage?.reveal_transition ?? 'fade_in';
	const is_scratch = (reveal_type == 'scratch') ? true : false;
	const item_separation = (is_scratch) ? 16 : 0;

	let group_pos = getAbsoluteCoords(stage.reveal_group_area, size);
	group_pos.width = group_pos.w;
	group_pos.height = group_pos.h;

	let square_width = Math.min(Math.floor((group_pos.width / cols)), group_pos.height);

	const generateGrid = () => {
		const grid = [];

		const num_rows = (stage.reveal_num == 1) ? 1 : cols;

		const h_separation = (group_pos.width - cols * square_width) / Math.max((cols - 1), 1);

		for(var i = 0; i < cols; i++) {
			for(var j = 0; j < num_rows; j++) {
				grid.push({
					x:i * (square_width + h_separation),
					y:j * square_width,
					w:square_width,
					h:square_width
				})
			}
		}

		return grid;
	};

	const paintUnder = (gridRef, index) => {
		let toDisplay;

		if(stage.reveal_mode == 'prizes') {
			toDisplay = images.current[path.current[userPath.current]];
		}

		if(stage.reveal_mode == 'win') {
			toDisplay = (path.current[userPath.current] == 1) ? images.current['prize'] : images.current[0];
		}

		const square_size = gridRef.current[index].width();

		const backImage = new Konva.Rect({
			width:square_size,
			height:square_size,
			x:(square_size + item_separation) / 2,
			y:(square_size + item_separation) / 2,
			offsetX:square_size / 2,
			offsetY:square_size / 2,
			opacity:1,
		});

		if(toDisplay) {
			const square_img = scaleImage(toDisplay, square_size);
			backImage.fillPatternImage(toDisplay.img);
			backImage.fillPatternRepeat('no-repeat');
			backImage.fillPatternScaleX(square_img);
			backImage.fillPatternScaleY(square_img);
			backImage.fillPatternOffsetX(square_img / 2);
			backImage.fillPatternOffsetY(square_img / 2);
		}

		prizeGridRef.current[`${index}`] = backImage;
		gridUnderRef.current[index].add(backImage);
		prizeGridRef.current[`${index}`].cache(default_cache_props);
		refs.current['paint_layer_ref'].draw();
	};

	const revealAnimation = (index, is_autoopen) => {
		if(is_autoopen == undefined) is_autoopen = false;

		let toDisplay;
		if(stage.reveal_mode == 'prizes') {
			toDisplay = images.current[path.current[userPath.current]];
		}

		if(stage.reveal_mode == 'win') {
			toDisplay = (path.current[userPath.current] == 1) ? images.current['prize'] : images.current[0];
		}

		if(is_autoopen) {
			auto_open_items.current.push(index);
		}

		const square_size = gridRef.current[index].width();

		let backImage = new Konva.Rect({
			width:square_size,
			height:square_size,
			x:(square_size + item_separation) / 2,
			y:(square_size + item_separation) / 2,
			offsetX:square_size / 2,
			offsetY:square_size / 2,
			opacity:(reveal_type == 'fade_in') ? 0 : 1,
		});

		if(toDisplay) {
			const square_img = scaleImage(toDisplay, square_size);
			backImage.fillPatternImage(toDisplay.img);
			backImage.fillPatternRepeat('no-repeat');
			backImage.fillPatternScaleX(square_img);
			backImage.fillPatternScaleY(square_img);
			backImage.fillPatternOffsetX(square_img / 2);
			backImage.fillPatternOffsetY(square_img / 2);
		}

		prizeGridRef.current[`${index}`] = backImage;
		gridUnderRef.current[index].add(backImage);
		gridUnderRef.current[index].setZIndex(0);
		gridRef.current[index].setZIndex(1);

		if(reveal_type == 'fade_in') {
			gridRef.current[index].to({
				opacity:0,
				duration:0.75,
			});

			prizeGridRef.current[index].to({
				opacity:1,
				duration:0.75,
				onFinish:() => {
					prizeGridRef.current[`${index}`].cache(default_cache_props);
					refs.current['paint_layer_ref'].draw();
				},
			});
		} else {
			gridRef.current[index].opacity(0);
			prizeGridRef.current[`${index}`].cache(default_cache_props);
			refs.current['paint_layer_ref'].draw();
		}
	};
	
	const revealSquare = () => {
		const index = scratching_index.current;

		if(grid.current[index] == undefined) return;
		if(grid.current[index].revealed) return;
		if(hits.current >= max_tries || (hits.current == items_to_reveal && prizeType.id != 0)) return;
		if(userPath.current > (path.current.length - 1)) return;
		grid.current[index].revealed = true;

		revealAnimation(index);
		refs.current['paint_layer_ref'].draw();

		if(path.current[userPath.current] == 1 || (path.current[userPath.current] == prizeType.id )) {
			hits.current++;
			if(path.current[userPath.current] == prizeType.id) hit_items.current.push(index);
		}

		userPath.current++;

		scratching_index.current = false;

		if((hits.current == items_to_reveal && prizeType.id != 0) || (hits.current >= max_tries) || (userPath.current > (path.current.length - 1))) {
			if(prizeType.id != 0) {
				executeFinishAnimation();
				soundOK();
			} else {
				soundKO();
				finalReveal();
				if(props.onHit != undefined) props.onHit(false);
			}

			if(props.onFinish) props.onFinish();

			if(stage.reveal_hide_final != undefined && stage.reveal_hide_final) {
				refs.current['group'].opacity(0);
				refs.current['paint_layer_ref'].draw();
			}

			setTimeout(() => {
				finalTextAnimation(((prizeType.id != 0) ? true : false), () => {
					if(props.onFinishGame) props.onFinishGame();
				});
			}, 2500);
		} else {
			const is_ok = (path.current[userPath.current - 1] != '0') ? true : false;
			(is_ok) ? soundIsChestOK() : soundIsChestKO();
			if(props.onHit != undefined) props.onHit(is_ok);
		}
	};

	const finalTextAnimation = (has_prize, cb) => {
		const font_size = 40;
		const font_size_scale = size / standard_board_size;

		const message_ok = (stage.reveal_message_ok != undefined && stage.reveal_message_ok != "") ? stage.reveal_message_ok : "YOU WIN";
		const message_ko = (stage.reveal_message_ko != undefined && stage.reveal_message_ko != "") ? stage.reveal_message_ko : "Oops";
		
		const final_text_props = {
			width:size,
			height:font_size * font_size_scale,
			text:i18n((has_prize) ? message_ok : message_ko),
			fontSize:font_size * font_size_scale,
			lineHeight:1,
			align:'center',
			fill:'#FFFFFF',
		};
		
		const final_text_stroke = new Konva.Text({ ...final_text_props, strokeWidth:3, stroke:'#000000', lineCap:'round', lineJoin:'round' });
		const final_text_fill = new Konva.Text(final_text_props);
		
		const final_text = new Konva.Group({
			width:size,
			height:font_size * font_size_scale,
			scaleX:0,
			scaleX:0,
			offsetX:size / 2,
			offsetY:(font_size / 2) * font_size_scale,
			x:size / 2,
			y:size / 2,
		});
		final_text.add(final_text_stroke);
		final_text.add(final_text_fill);

		refs.current['paint_layer_ref'].add(final_text);
		refs.current['final_text'] = final_text;

		refs.current['final_text'].to({
			scaleX:2,
			scaleY:2,
			duration:1,
			easing:Konva.Easings.BounceEaseOut,
			onFinish:() => {
				setTimeout(() => {
					refs.current['final_text'].to({
						scaleX:0,
						scaleY:0,
						duration:1,
						easing:Konva.Easings.StrongEaseInOut,
						onFinish:() => {
							if(cb != undefined) cb();
						},
					});
				}, 2000);
			},
		});
	};

	const finalReveal = () => {
		let transparency_array = [];
		if(stage.reveal_mode == 'prizes') {
			const prize_ids = stage.scratch.map((p) => (p.id));
			for(var i = path.current.length; i < Math.pow(cols,2); i++) {
				transparency_array.push(prize_ids[i % prize_ids.length]);
			}
		} else {
			transparency_array.push(1);
			for(var i = path.current.length+1; i < Math.pow(cols,2); i++) {
				transparency_array.push(i % 2 == 0 ? 1 : 0);
			}
		}

		shuffleArray(transparency_array);

		path.current = path.current.concat(transparency_array);
		for(var i = 0; i < grid.current.length; i++) {
			if(grid.current[i].revealed) continue;
			revealAnimation(i, true);
			userPath.current++;
		}

		auto_open_items.current.map((item) => {
			prizeGridRef.current[`${item}`].filters([Konva.Filters.HSL]);
		});

		auto_open_items.current.map((item) => {
			toggleNotOpenedItems(item);
		});

		if(refs.current['scratch_layer_ref'] != undefined) {
			refs.current['scratch_layer_ref'].to({
				opacity:0,
				duration:0.2,
			});
		}
		refs.current['paint_layer_ref'].draw();
	};

	const executeFinishAnimation = () => {
		finalReveal();

		hit_items.current.map((item) => {
			prizeGridRef.current[`${item}`].filters([Konva.Filters.HSL]);
		});

		hit_items.current.map((item) => {
			toggleHighlightedItems(item, 1, 0);
		});
	};

	const toggleNotOpenedItems = (item) => {
		prizeGridRef.current[`${item}`].to({
			saturation:-100,
			scaleX:0.7,
			scaleY:0.7,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
		});
	};

	const toggleHighlightedItems = (item, dir, count) => {
		const _count = (dir > 0) ? (count + 1) : count;
		prizeGridRef.current[`${item}`].to({
			luminance:(dir > 0) ? 0.6 : 0,
			scaleX:(dir > 0) ? 1.06 : 1,
			scaleY:(dir > 0) ? 1.06 : 1,
			duration:0.2,
			easing:Konva.Easings.StrongEaseInOut,
			onFinish:() => {
				if(count < end_animation_max_count) {
					const _dir = (dir > 0) ? -1 : 1;
					toggleHighlightedItems(item, _dir, _count);
				}
			}
		});
	};

	const getCanvasContext = () => {
		if(refs.current['paint_layer_ref'] == undefined) return false;
		return refs.current['paint_layer_ref'].getCanvas().getContext();
	};

	const prizeCandidates = (prize) => {
		let candidates = (stage.scratch != undefined) ? stage.scratch.filter((p) => {
			return (p.id != prize);
		}).map((p) => (p.id)) : [0];
		return candidates;
	};

	const generatePath = (prize) => {
		const max = max_tries;
		const min = items_to_reveal;
		const moves = Math.random() * (max - min) + min;
		const path = [];
		
		if(items_to_reveal == 1) {
			let revealed_prize = 1;
			if(stage.reveal_mode == 'prizes') {
				revealed_prize = (prize == false) ? shuffleArray(prizeCandidates(prize))[0] : prize;
			}
			
			if(prize == false) {
				for(var i = 0; i < max_tries; i++) path.push(0);
				
				const theprize = [revealed_prize];
				for(var j = max_tries + 1; j < stage.reveal_size; j++) theprize.push(0);

				return path.concat(shuffleArray(theprize));
			}

			path.push((prize == false) ? 0 : ((stage.reveal_mode == 'win') ? 1 : prize));
			for(var i = 0; i < moves - 1; i++) path.push(0);
		} else {
			if(stage.reveal_mode == 'win') {
				if(prize == false) {
					for(var i = 0; i < max_tries - 3; i++) path.push(0);
					for(var i = 0; i < 2; i++) path.push(1);
					const caminito = shuffleArray(path).concat([0]);
					return caminito;
				} else {
					for(var i = 0; i < 3; i++) path.push(1);
					let len = path.length;
					for(var i = 0; i < moves - len; i++) path.push(0);
				}
			}

			if(stage.reveal_mode == 'prizes') {
				let candidates = (stage.scratch != undefined) ? stage.scratch.filter((p) => {
					return (p.id != prize);
				}).map((p) => (p.id)) : [0];

				//Fail path
				if(prize == false) {
					let shuffled = shuffleArray(candidates);
					const posible_prize = shuffled.shift();
				
					candidates = [posible_prize,posible_prize];
					shuffled = shuffled.concat(shuffled);
					candidates = candidates.concat(shuffleArray(shuffled));

					for(let i = 0; i < moves - 2; i++) {
						if(candidates[i]==undefined) {
							path.push(0);
							continue;
						}
						path.push(candidates[i]);
					}
					for(var i = path.length; i < max_tries; i++) path.push(prize);

					const shuffled_path = shuffleArray(path);

					if(shuffled_path[shuffled_path.length - 1] == posible_prize) {
						shuffled_path[shuffled_path.length - 1] = shuffled_path[shuffled_path.length - 2];
						shuffled_path[shuffled_path.length - 2] = posible_prize;
					}

					if(shuffled_path[shuffled_path.length - 1] == posible_prize && shuffled_path[shuffled_path.length - 2] == posible_prize) {
						const old = shuffled_path[shuffled_path.length - 3];
						shuffled_path[shuffled_path.length - 3] = posible_prize;
						shuffled_path[shuffled_path.length - 1] = old;
					}

					return shuffled_path;
				} else {
					//Prize path
					candidates = shuffleArray(candidates.concat(candidates));
					for(var i = 0; i < 3; i++) path.push(prize);
					let len = path.length;
					for(var i = 0; i < moves - len; i++) path.push(candidates[i]);
				}
			}
		}

		const shuffled_path = shuffleArray(path);
		if(shuffled_path.length < stage.reveal_size) {
			for(var i = shuffled_path.length; i < stage.reveal_size; i++) path.push(0);
		}

		return shuffled_path;
	};

	const loadPrizeImages = () => {
		const cover_image = stage?.full_scratch_cover ?? ((!is_scratch) ? `${default_images_path}chest_closed.png` : `/img/games_gui/scratch/tapper.jpg`);
		if(cover_image) {
			let img = loadKonvaImage(cover_image, () => {
				images.current['cover'] = {
					img,
					image_w:img.width,
					image_h:img.height,
				};

				setCoverLoaded(true);
			});
		}

		const img_url = `${window.cdn_url}img/games_gui/scratch/brush.png`;
		let img = loadKonvaImage(img_url, () => {
			brush_image.current = img;
		});
	
		if(stage.reveal_mode == 'win') {
			stage.reveal_win.map((prize) => {
				const image = (prize.scratch_image != undefined) ? prize.full_scratch_image : (
					(prize.id == 'prize') ? `${default_images_path}chest_prize.png` : `${default_images_path}chest_empty.png`
				);

				let img = loadKonvaImage(image, () => {
					images.current[prize.id] = {
						img,
						image_w:img.width,
						image_h:img.height,
					};
				});
			});
			return;
		}

		if(!stage?.scratch) return;

		stage.scratch.map((prize) => {
			if(prize.scratch_image == undefined) return;
			let img = loadKonvaImage(prize.full_scratch_image, () => {
				images.current[prize.id] = {
					img,
					image_w:img.width,
					image_h:img.height,
				};
			});
		});
	};

	useEffect(() => {
		setTimeout(() => {
			setForceFlow(true);
		}, 100);
	}, []);

	const reset = (forced) => {
		if(userPath.current == 0 && !forced) return;
		hit_items.current = [];
		userPath.current = 0;
		hits.current = 0;
		setIsGameFinished(true);
		setTimeout(() => setIsGameFinished(false), 0);
	};

	const setPath = (prize) => {
		path.current = generatePath(prize);
	};

	useEffect(() => {
		if(props.canvas_size != undefined) {
			loadPrizeImages();
			scale.current = props.canvas_size / standard_board_size;
			setSize(props.canvas_size);
		}
	}, [ props.canvas_size ]);

	useEffect(() => {
		grid.current = generateGrid();
	}, [ group_pos ]);

	useEffect(() => {
		setPath(props.prizeType.id);
		if(is_builder) {
			hits.current = 0;
			reset(true);
		}
	}, [props.prizeType, stage]);

	useEffect(() => {
		loadPrizeImages();
	}, [ stage.reveal_mode, stage ]);

	useEffect(() => {
		setPath(props.prizeType.id);
	}, []);

	const generateManual = () => {
		if(stage.reveal_individual_area) return stage.reveal_individual_area;
		return grid.current.map((g) => {
			if(editing_mode == 'block') return getRelativeCoords(g, size)
			let new_g = {...g};
			new_g.x += group_pos.x;
			new_g.y += group_pos.y;
			return getRelativeCoords(new_g, size)
		});
	};

	const upOutside = (e) => {
		revealSquare();
		refs.current['particles'].up(e);
	}

	const onStartAction = (index) => {
		if(scratching_index.current !== false) {
			// to avoid moving outside the canvas and clicking a new box (maximum piruling)
			upOutside();
		}

		scratching_index.current = index;
	};

	useEffect(() => {
		if(editing_mode == 'block') return;
		if(stage.reveal_individual_area != undefined && stage.reveal_individual_area != '') return;
		const areas = generateManual();
		props.areaChanged('full',areas);
	}, [ stage.reveal_individual_area, editing_mode ]);

	let manual_pos = generateManual();

	const moveGeneral = (area) => {
		const abs = getAbsoluteCoords(area,size);
		refs.current.group.setAttrs(abs);
		refs.current.paint_layer_ref.draw();
	};

	const moveIndividual = (item_index, area) => {
		const abs = getAbsoluteCoords(area, size);
		gridUnderRef.current[item_index].setAttrs(abs);
		const s = Math.min(abs.w, abs.h);
		const cover_img_scale  = scaleImage(images.current['cover'], s - item_separation);
		gridRef.current[item_index].setAttrs({
			width:s,
			height:s,
			fillPatternScaleX:cover_img_scale,
			fillPatternScaleY:cover_img_scale,
			fillPatternOffsetX:cover_img_scale / 2,
			fillPatternOffsetY:cover_img_scale / 2,
		})

		refs.current.paint_layer_ref.draw();
	};

	if(!cover_loaded) return null;

	if(is_game_finished) return null;

	const areas_group_pos = getAbsoluteCoords(((editing_mode == 'block' && stage.reveal_group_area != undefined) ? stage.reveal_group_area : { w:1, h:1, x:0, y:0 }), size);

	return (
		<Stage
			ref={(node) => { refs.current['main_stage'] = node; }}
			className="canvas_cont"
			width={size}
			height={size}
		>
			<Layer ref={(node) => { refs.current['paint_layer_ref'] = node; }}>
				<Rect
					width={size}
					height={size}
					opacity={0}
					fill="black"
					onMouseUp={upOutside}
					onTouchEnd={upOutside}
				/>

				<Group ref={(node) => { refs.current['group'] = node; }} {...areas_group_pos}>
					{grid.current.map((square, index) => {
						const abs = getAbsoluteCoords(manual_pos[index], size);
						const x = abs?.x ?? square.x;
						const y = abs?.y ?? square.y;
						const w = abs?.w ?? square_width;
						const h = abs?.h ?? square_width;
						const s = Math.min(w, h);

						const cover_img_scale  = scaleImage(images.current['cover'], s - item_separation);

						return (
							<Group
								key={index}
								ref={(node) => { gridUnderRef.current[index] = node; }}
								x={x}
								y={y}
								width={s}
								height={s}
								scaleX={1}
								scaleY={1}
							>
								{(!is_scratch || !is_playable) && (
									<Rect
										ref={(node) => { gridRef.current[index] = node; }}
										width={s - item_separation}
										height={s - item_separation}
										offsetX={s / 2}
										offsetY={s / 2}
										x={(s + item_separation) / 2}
										y={(s + item_separation) / 2}
										fillPatternImage={images.current['cover'].img}
										fillPatternRepeat='no-repeat'
										fillPatternScaleX={cover_img_scale}
										fillPatternScaleY={cover_img_scale}
										fillPatternOffsetX={cover_img_scale / 2}
										fillPatternOffsetY={cover_img_scale / 2}
										onMouseDown={() => onStartAction(index)}
										onTouchStart={() => onStartAction(index)}
										onMouseUp={revealSquare}
										onTouchEnd={revealSquare}
										cornerRadius={(is_scratch) ? 10 : 0}
										stroke={(is_scratch) ? 'rgba(0, 0, 0, 0.5)' : null}
										strokeWidth={(is_scratch) ? 1 : null}
									/>
								)}
							</Group>
						)
					})}
				</Group>

				{(!is_playable || show_grid) && (
					<Rect width={size} height={size} opacity={0} fill="black" />
				)}

				{(show_grid) && (
					<Group>
						{(editing_mode == 'block') && (
							<EditableArea
								areaMove={moveGeneral}
								area={stage.reveal_group_area}
								areaChanged={(area) => props.areaChanged('group', area)}
								guides_color={guides_color}
								is_editing={show_grid}
								size={size}
							/>
						)}

						{(editing_mode == 'manual') && (
							<Group {...areas_group_pos}>
								{manual_pos.map((pos, item_index) => (
									<EditableArea
										key={`area_${item_index}`}
										ref={(node) => { refs.current[`area_${item_index}`] = node; }}
										areaMove={(area) => moveIndividual(item_index, area)}
										area={pos}
										areaChanged={(area) => props.areaChanged(item_index, manual_pos.map((a, ind) => {
											return (ind != item_index) ? a : area;
										}))}
										proportion={{ w:1, h:1 }}
										guides_color={guides_color}
										is_editing={show_grid}
										size={size}
									/>
								))}
							</Group>
						)}
					</Group>
				)}
			</Layer>

			{(is_scratch && is_playable) && (
				<Layer ref={(node) => { refs.current['scratch_layer_ref'] = node; }}>
					<Group
						{...areas_group_pos}
						onMouseUp={upOutside}
						onTouchEnd={upOutside}
					>
						{grid.current.map((square, index) => {
							const abs = getAbsoluteCoords(manual_pos[index], size);
							const x = abs?.x ?? square.x;
							const y = abs?.y ?? square.y;
							const w = abs?.w ?? square_width;
							const h = abs?.h ?? square_width;
							const s = Math.min(w, h);

							const cover_img_scale = scaleImage(images.current['cover'], s - item_separation);

							return (
								<Group
									key={index}
									ref={(node) => { scratchRef.current[index] = node; }}
									x={x}
									y={y}
									width={s}
									height={s}
									scaleX={1}
									scaleY={1}
								>
									<ScratchableElement
										thescale={scale.current}
										refs={refs}
										item_grid={gridRef}
										brush_image={brush_image}
										reference={(node) => { gridRef.current[index] = node; }}
										width={s - item_separation}
										height={s - item_separation}
										offsetX={s / 2}
										offsetY={s / 2}
										x={(s + item_separation) / 2}
										y={(s + item_separation) / 2}
										fillPatternImage={images.current['cover'].img}
										fillPatternRepeat='no-repeat'
										fillPatternScaleX={cover_img_scale}
										fillPatternScaleY={cover_img_scale}
										fillPatternOffsetX={cover_img_scale / 2}
										fillPatternOffsetY={cover_img_scale / 2}
										onMouseUp={revealSquare}
										onTouchEnd={revealSquare}
										paintUnder={(index) => paintUnder(gridRef, index)}
										index={index}
										canvas_scale={scale.current}
										cornerRadius={(is_scratch) ? 10 : 0}
										stroke='rgba(0, 0, 0, 0.5)'
										strokeWidth={1}
										item_separation={item_separation}
										onStartAction={onStartAction}
									/>
								</Group>
							)
						})}
					</Group>
				</Layer>
			)}

			<ExternalParticles
				stage={refs.current['main_stage']}
				ref={(node) => { refs.current['particles'] = node; }}
				canvas_context={getCanvasContext()}
				params={{
					Gravity:0.2,
				}}
			/>
		</Stage>
	)
};

export default injectIntl(RevealWinCanvas);